import _ from 'lodash';
import React, {FC, useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {usePrevious} from '../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {isDefined} from '../../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {CancelError} from '../../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {reportToBugsnag} from '../../../../../../srcCommon/services/bugsnag/bugsnag';
import {RandomSkeleton} from '../../../../../components/common/randomSkeleton';
import {
  getNameForCard,
  isEnhancedPrivateCard,
  selectCardViaPriorityOrder,
} from '../../../../../helpers/models/cardHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {CardModel} from '../../../../../models/api/cardModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {ApiMessagesDataGroup} from '../../../../../models/api/dataGroupModel';
import {RecipientRolesEnum} from '../../../../../models/api/recipientModel';
import {fetchApiAsync} from '../../../../../services/http/http';
import {HttpError} from '../../../../../services/http/httpError';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {SidePanelSubSectionWrapper} from '../sidePanelSubSectionWrapper';

/*
 * Props.
 */

interface SummaryContactsProps {
  selectedConversation: ConversationModel;
}

/*
 * Constants.
 */

const visibleRecipientRoles = [
  RecipientRolesEnum.TO,
  RecipientRolesEnum.CC,
  RecipientRolesEnum.FROM,
  RecipientRolesEnum.REPLY_TO,
];

/*
 * Styles.
 */

const StyledContactsDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const StyledContactNamesDiv = styled.div`
  font-size: ${fontSizes.medium};
  ${ellipsis()};
`;

const StyledContactNamesTooltipDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledContactTooltipDiv = styled.div`
  padding: 1px 0px;
`;

/*
 * Component.
 */

export const SummaryContacts: FC<SummaryContactsProps> = ({selectedConversation}) => {
  const company = useAppSelector(getCompany);
  const previousConversation = usePrevious(selectedConversation);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [conversationCards, setConversationCards] = useState<ReadonlyArray<CardModel>>([]);

  const fetchConversationCards = useCallback(async () => {
    try {
      // This endpoint is limited to 100 messages, which prevents us from trying to load
      // too much data from some massive message chain. This also means that we might not have
      // every contact on a conversation, but that seems like a fine tradeoff since the contact
      // details panel makes the same tradeoff, just by using every message loaded in P3 for its
      // calculation of the conversation contacts.
      const basePath = `/conversations/${selectedConversation.id}/messages`;
      const {messages, cards} = await fetchApiAsync(ApiMessagesDataGroup, `${company.url}${basePath}`);

      const cardsResourcesMap = buildSummaryCardResourceMap(cards);

      const recipientCards: ReadonlyArray<CardModel> = _(messages)
        .map((m) => m.recipients)
        .flatten()
        .uniqBy((rp) => rp.contactUid)
        .filter((rp) => visibleRecipientRoles.includes(rp.role))
        .map((rp) => cardsResourcesMap[rp.contactUid])
        .filter((card): card is CardModel => isDefined(card))
        .value();

      setConversationCards(recipientCards);
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }

    setHasLoaded(true);
  }, [company.url, selectedConversation.id]);

  useEffect(() => {
    // Initial load or new conversation selected
    if (!previousConversation || selectedConversation.id !== previousConversation.id) {
      setConversationCards([]);
      setHasLoaded(false);
      fetchConversationCards();
    }
  }, [fetchConversationCards, previousConversation, selectedConversation.id]);

  if (hasLoaded && conversationCards.length === 0) {
    return null;
  }

  return (
    <SidePanelSubSectionWrapper title={<Trans id="analytics-drawer-details-contacts">Contacts</Trans>}>
      <StyledContactsDiv>
        {conversationCards.length > 0 && (
          <TooltipCoordinator render={() => renderContactNamesForTooltip(conversationCards)}>
            <StyledContactNamesDiv>{conversationCards.map(getNameForCard).join(', ')}</StyledContactNamesDiv>
          </TooltipCoordinator>
        )}
        {!hasLoaded && conversationCards.length === 0 && <RandomSkeleton />}
      </StyledContactsDiv>
    </SidePanelSubSectionWrapper>
  );
};

/*
 * Helpers.
 */

function renderContactNamesForTooltip(cards: ReadonlyArray<CardModel>) {
  return (
    <StyledContactNamesTooltipDiv>
      {cards.map((card) => (
        <StyledContactTooltipDiv key={`contact-${card.id}`}>{getNameForCard(card)}</StyledContactTooltipDiv>
      ))}
    </StyledContactNamesTooltipDiv>
  );
}

function buildSummaryCardResourceMap(cards?: ReadonlyArray<CardModel>) {
  // First, group all the cards by their contactUID
  const cardsByContactUidMap: Record<string, CardModel[]> = {};
  cards?.forEach((card) =>
    card.contactUids.forEach((contactUid: string) => {
      if (!cardsByContactUidMap[contactUid]) {
        cardsByContactUidMap[contactUid] = [card];
      } else {
        cardsByContactUidMap[contactUid] = [...cardsByContactUidMap[contactUid], card];
      }
    }),
  );

  // Next, filter out any private cards, since we only want to display global cards in analytics.
  // Then use the priority order defined in the card helpers to pick the card we display.
  const cardsByContactUidFilteredMap: Record<string, CardModel> = {};
  Object.entries(cardsByContactUidMap).forEach(([contactUid, cardsForContactUid]) => {
    const filteredCards = cardsForContactUid.filter((card) => !isEnhancedPrivateCard(card));
    const priorityCard = selectCardViaPriorityOrder(filteredCards);

    cardsByContactUidFilteredMap[contactUid] = priorityCard;
  });

  return cardsByContactUidFilteredMap;
}
